import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import Datetime from "react-datetime";
import moment from "moment-timezone";
import "react-datetime/css/react-datetime.css";
import { setColor } from "../../../../assets/styles/styles";
import Typography from "../../../../components/common/text/Typography";
import Banner from "../../../../components/layout/ui/Banner";
import { default as Button } from "../../../../components/common/button/Button";
import LoadingIcon from "../../../../components/common/loading/LoadingIcon";
import Dropdown from "../../../../components/common/inputDropdown/Dropdown";
import { addAchievement } from "../../../../api/AchievementController";
import { getAllClientsTag } from "../../../../api/ClientsTagController";
import { TIEREDCOUNTJSON, CACHE_QUERY_OPTIONS } from "../../../../constants";

const AddAchievement = ({ closeModal, refetchAchievements }) => {
    const timezone = moment.tz.guess();
    const minDate = moment().startOf("day").tz(timezone);
    const [bannerType, setBannerType] = useState("");
    const [successMessageBanner, setSuccessMessageBanner] = useState("");
    const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
    const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);
    const [achievementState, setAchievementState] = useState({
        title: "",
        description: "",
        type: "",
        metaData: {},
        stages: {},
        icons: {},
        startDate: null,
        endDate: null,
        clientTag: "",
    });

    const {
        data: clientTags,
        error,
        isLoading,
        refetch,
    } = useQuery(["clientTags"], () => getAllClientsTag(), {
        ...CACHE_QUERY_OPTIONS(),
    });

    useEffect(() => {
        console.log("Client Tags:", clientTags);
        if (successMessageBanner) {
            const timer = setTimeout(() => {
                setSuccessMessageBanner("");
                setBannerType("");
                refetchAchievements?.();
                closeModal();
                setAchievementState({
                    title: "",
                    description: "",
                    type: "",
                    metaData: {},
                    stages: {},
                    icons: {},
                    startDate: null,
                    endDate: null,
                    clientTag: "",
                });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessageBanner, refetchAchievements, closeModal]);

    const AchvTypes = [
        { id: "OneTime", value: "OneTime" },
        { id: "TieredCount", value: "TieredCount" },
        { id: "TieredTime", value: "TieredTime" },
    ];

    const achievementStateHandler = (key, value) => {
        setAchievementState((prevState) => ({
            ...prevState,
            [key]: value,
        }));
    };

    const handleStartDateChange = (date) => {
        if (moment(date).isSameOrAfter(moment(), "day")) {
            achievementStateHandler("startDate", date);
        }
    };

    const handleAddAchievement = async (e) => {
        e.preventDefault();
        const { startDate, endDate } = achievementState;

        const today = moment().startOf("day"); // local today, no UTC
        const start = moment(startDate).startOf("day");
        const end = moment(endDate).startOf("day");

        if (start.isAfter(end)) {
            setBannerType("ERROR");
            setSuccessMessageBanner("Start date cannot be after end date.");
            return;
        }

        if (start.isBefore(today) || end.isBefore(today)) {
            setBannerType("ERROR");
            setSuccessMessageBanner("Start and End dates cannot be in the past.");
            return;
        }

        try {
            const resp = await addAchievement(achievementState);
            if (resp?.status === 201) {
                setBannerType("SUCCESS");
                setSuccessMessageBanner("Achievement added successfully!");
            } else {
                setBannerType("ERROR");
                setSuccessMessageBanner(resp?.data?.message || "Failed to add achievement. Please try again.");
            }
        } catch (error) {
            console.error("Error adding achievement:", error);
            setBannerType("ERROR");
            setSuccessMessageBanner("Failed to add achievement. Please try again.");
        }
    };

    return (
        <>
            <Banner
                width={"100%"}
                zIndex="1000"
                message={`${successMessageBanner}`}
                type={bannerType}
                openBanner={!!successMessageBanner}
                setOpenBanner={setSuccessMessageBanner}
                topDisplay
                showCloseButton
                gameScreen={false}
                top={"0px"}
                left={"0"}
            />

            <ModalContent>
                <GeneralContainer>
                    <Typography variant="h2PurpleHeading" margin={`0 0 0 0`}>
                        ADD ACHIEVMENT
                    </Typography>

                    <Card>
                        <Table>
                            <TableRow>
                                <TableColumn>
                                    <Label>Title*</Label>
                                    <InputBox
                                        type="text"
                                        id="title"
                                        placeholder="Enter Title"
                                        value={achievementState.title}
                                        onChange={(e) => achievementStateHandler("title", e.target.value)}
                                    />
                                </TableColumn>
                            </TableRow>
                            <TableRow>
                                <TableColumn>
                                    <Label>Description*</Label>
                                    <InputBox
                                        type="text"
                                        id="description"
                                        placeholder="Enter Description"
                                        value={achievementState.description}
                                        onChange={(e) => achievementStateHandler("description", e.target.value)}
                                    />
                                </TableColumn>
                            </TableRow>

                            <TableRow>
                                <TableColumn>
                                    <Dropdown
                                        key={"type"}
                                        label="Type*"
                                        items={AchvTypes?.map((item) => ({
                                            ...item,
                                        }))}
                                        selectedValue={achievementState.type}
                                        setSelectedValue={(value) => achievementStateHandler("type", value)}
                                        placeholder="Select Type"
                                    />
                                </TableColumn>
                            </TableRow>

                            {achievementState.type === "TieredCount" && (
                                <>
                                    <TableRow>
                                        <TableColumn>
                                            <Label>Stages*</Label>
                                            <InputBox
                                                type="text"
                                                id="stages"
                                                placeholder="Enter Stages"
                                                value={JSON.stringify(TIEREDCOUNTJSON)}
                                                onChange={(e) => {
                                                    try {
                                                        const parsed = JSON.parse(e.target.value);
                                                        achievementStateHandler("stages", parsed);
                                                    } catch {
                                                        console.error("Invalid JSON format for stages");
                                                    }
                                                }}
                                            />
                                        </TableColumn>
                                    </TableRow>
                                </>
                            )}

                            <TableRow>
                                <TableColumn>
                                    <Dropdown
                                        key={"ClientTag"}
                                        label="Client Tag*"
                                        items={clientTags?.map((item) => ({
                                            id: item.tag,
                                            value: item.tag,
                                        }))}
                                        selectedValue={achievementState.clientTag}
                                        setSelectedValue={(value) => achievementStateHandler("clientTag", value)}
                                        placeholder="Select Client Tag"
                                    />
                                    <YellowText> intraverse is the default client tag.</YellowText>
                                </TableColumn>
                            </TableRow>

                            <TableRow>
                                <TableColumn>
                                    <Label>Start Date</Label>
                                    <CustomDatetime
                                        value={
                                            achievementState.startDate
                                                ? moment(achievementState.startDate).tz(timezone)
                                                : ""
                                        }
                                        onChange={(date) => {
                                            const localMidnight = moment(date).tz(timezone).startOf("day").format(); // ISO string in local time
                                            setAchievementState((prev) => ({
                                                ...prev,
                                                startDate: localMidnight,
                                            }));
                                            setIsStartCalendarOpen(false);
                                        }}
                                        dateFormat="YYYY-MM-DD"
                                        timeFormat={false}
                                        open={isStartCalendarOpen}
                                        inputProps={{
                                            placeholder: "Choose Start Date",
                                            value: achievementState.startDate
                                                ? moment(achievementState.startDate).tz(timezone).format("YYYY-MM-DD")
                                                : "",
                                            onClick: () => setIsStartCalendarOpen(true),
                                            readOnly: true,
                                        }}
                                    />
                                </TableColumn>
                            </TableRow>
                            <TableRow>
                                <TableColumn>
                                    <Label>End Date</Label>
                                    <CustomDatetime
                                        value={
                                            achievementState.endDate
                                                ? moment(achievementState.endDate).tz(timezone)
                                                : ""
                                        }
                                        onChange={(date) => {
                                            const localMidnight = moment(date).tz(timezone).startOf("day").format(); // ISO string in local time
                                            setAchievementState((prev) => ({
                                                ...prev,
                                                endDate: localMidnight,
                                            }));
                                            setIsEndCalendarOpen(false);
                                        }}
                                        dateFormat="YYYY-MM-DD"
                                        timeFormat={false}
                                        open={isEndCalendarOpen}
                                        inputProps={{
                                            placeholder: "Choose End Date",
                                            value: achievementState.endDate
                                                ? moment(achievementState.endDate).tz(timezone).format("YYYY-MM-DD")
                                                : "",
                                            onClick: () => setIsEndCalendarOpen(true),
                                            readOnly: true,
                                        }}
                                    />
                                </TableColumn>
                            </TableRow>

                            <TableRow>
                                <TableColumn>
                                    <Label>Meta Data*</Label>
                                    <InputBox
                                        type="text"
                                        id="metaData"
                                        placeholder="Enter Meta Data"
                                        value={
                                            achievementState.metaData ? JSON.stringify(achievementState.metaData) : ""
                                        }
                                        onChange={(e) => achievementStateHandler("metaData", e.target.value)}
                                    />
                                </TableColumn>
                            </TableRow>

                            <TableRow>
                                <TableColumn>
                                    <Label>Icons*</Label>
                                    <InputBox
                                        type="text"
                                        id="icons"
                                        placeholder="Enter Icons"
                                        value={achievementState.icons ? JSON.stringify(achievementState.icons) : ""}
                                        onChange={(e) => achievementStateHandler("icons", e.target.value)}
                                    />
                                </TableColumn>
                            </TableRow>
                            <TableRow>
                                <Button
                                    type="submit"
                                    variant={"PinkButton"}
                                    buttonWidth={"100%"}
                                    disabled={
                                        !achievementState.title ||
                                        !achievementState.description ||
                                        !achievementState.type
                                    }
                                    label={"Add Achievement"}
                                    onClick={handleAddAchievement}
                                />
                            </TableRow>
                        </Table>
                    </Card>
                </GeneralContainer>
            </ModalContent>
        </>
    );
};

export default AddAchievement;

const LoadingIconContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
`;

const YellowText = styled.span`
    color: ${setColor.yellowText};
    font-weight: 500;
    font-style: italic;
    font-size: 14px;
`;
const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    color: ${setColor.inputText};
`;

const Card = styled.div`
    width: 100%;
    height: auto;
    position: relative;
    padding: 20px;
    @media (max-width: 768px) {
        width: 100%;
        padding: 15px 0;
    }
`;

const GeneralContainer = styled.div`
    width: 100%;
    font-family: "Barlow-Regular", Helvetica;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const Table = styled.form`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    background: ${setColor.backgroundGrey};
    border-radius: 5px;
    margin-top: 15px;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const CustomDatetime = styled(Datetime)`
    .rdtDisabled {
        cursor: not-allowed !important;
        color: #ccc;
    }
    .rdt {
        background-color: #504c5c;
    }
    .rdtPicker {
        background-color: #504c5c;
        width: 100%;
        border-radius: 5px;
    }
    .rdtTimeToggle {
        background-color: #504c5c;
    }
    .rdtDays {
        background-color: #504c5c;
    }
    .rdtTime {
        background-color: #504c5c;
    }
    input {
        padding: 10px;
        border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
        border-radius: 5px;
        color: ${setColor.inputText};
        margin: 0rem 0rem 0rem 0rem;
        width: ${(props) => (props.width ? props.width : "100%")};
        background-color: transparent;
        margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
        &:disabled {
            background-color: ${setColor.grey1};
        }
    }
`;
